import type { ReactElement, ReactNode } from 'react';

import { HelpLabel } from './InfoTip';

/* A bounded region with an optional header. The unit every screen composes from.
 *
 * `flush` exists for the one case that needs it: a card whose only child is a table. The table draws
 * its own row separators, so the usual body padding leaves a visible gap between the header rule and
 * the first row — and a screen working around that with a negative margin is how a design system
 * starts being fought rather than used.
 */

export interface CardProps {
  /* `| undefined` on every optional prop. `exactOptionalPropertyTypes` is on, so a caller
   * forwarding a conditional — `count={ready ? label : undefined}` — cannot pass explicit
   * undefined to a bare `prop?: T`, and every screen does exactly that. */
  readonly title?: string | undefined;
  /** What the title means, behind the "i" beside it. See `InfoTip`. */
  readonly help?: string | undefined;
  /** Right-aligned in the header. A count, a timestamp — context, not a control.
   *
   * **`ReactNode` rather than `string`, widened for the scheduling cards.** Those carry a state
   * chip on the title baseline — Enabled / Disabled / Suspended until … — and the alternative was a
   * hand-built header row inside the body, which would put the chip below the rule the header draws
   * and leave one card in the detail grid with a different anatomy from its two neighbours.
   *
   * Still context and still not a control: a node here is a badge or a formatted figure, never a
   * button. Every existing caller passes a string and is unaffected — `ReactNode` includes it.
   */
  readonly hint?: ReactNode;
  readonly flush?: boolean;
  /** An entrance, for a card that is mounted in response to something the reader just did.
   *
   * **Opt-in rather than automatic, and the default is the reason.** Most cards are on the page
   * when it loads, and animating those means a screen that assembles itself on every navigation —
   * twelve panels arriving in sequence is a loading state nobody asked for, on a product whose
   * screens are read rather than watched.
   *
   * What this is for is the other case: a panel that appears *because a button was pressed*, like
   * the rename form on a rule. Those mount with no transition and push everything below them down
   * in a single frame, so the page arrives at a new shape with nothing connecting it to the old
   * one — the reader has to re-find their place. That is the inline half of "it feels old"; the
   * overlay half is `Modal`.
   *
   * Deliberately **not** a `className` passthrough. `Modal` records what that cost on `Button`: a
   * caller's class landed after the computed one and took the base class with it, leaving an
   * unstyled element that still carried the class the caller asked for. A boolean cannot do that,
   * and it also keeps the decision reviewable — `reveal` in a diff says what it means.
   */
  readonly reveal?: boolean | undefined;
  readonly children: ReactNode;
}

export function Card({
  title,
  help,
  hint,
  flush = false,
  reveal = false,
  children,
}: CardProps): ReactElement {
  return (
    <section className={reveal ? 'lq-card lq-card--reveal' : 'lq-card'}>
      {title !== undefined && (
        <div className="lq-card__header">
          {/* h2, because `PageHeader` owns the h1 on every screen. A card that chose its own level
              would produce a document outline that jumps from h1 to h3. */}
          <HelpLabel subject={title} help={help}>
            <h2 className="lq-card__title">{title}</h2>
          </HelpLabel>
          {hint !== undefined && <span className="lq-card__hint">{hint}</span>}
        </div>
      )}
      <div className={flush ? 'lq-card__body lq-card__body--flush' : 'lq-card__body'}>
        {children}
      </div>
    </section>
  );
}
